import { useState } from 'react'
import { SYSTEM_VERIFICATION_COPY } from '../constants/systemVerificationContent'

function detectClientPlatform() {
  const ua = String(navigator?.userAgent || '').toLowerCase()
  if (ua.includes('mac')) return 'macOS'
  return 'Windows'
}

export default function SetupGuidePanel({ downloadOptions = [] }) {
  const detectedOS = detectClientPlatform()
  const [activeTab, setActiveTab] = useState(detectedOS)
  const [copiedTerminal, setCopiedTerminal] = useState(false)

  const handleCopyTerminal = () => {
    navigator.clipboard.writeText(SYSTEM_VERIFICATION_COPY.setupMacTestingCommand)
    setCopiedTerminal(true)
    setTimeout(() => setCopiedTerminal(false), 2500)
  }

  const resolveDownloadName = (href, fallbackLabel) => {
    try {
      const url = new URL(href, window.location.origin)
      const raw = url.pathname.split('/').filter(Boolean).pop()
      if (raw) return raw
    } catch {
      // fallback below
    }
    return `${String(fallbackLabel || 'download').replace(/\s+/g, '-')}`
  }

  return (
    <div className="mx-8 mb-6 overflow-hidden rounded-lg border border-outline-variant/15 bg-surface-container-low shadow-sm">
      {/* Header with Title & Download Buttons */}
      <div className="border-b border-outline-variant/15 bg-surface-container-low px-6 py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-headline text-base font-bold text-on-surface">
              Desktop Agent Setup Guide
            </h3>
            <p className="text-xs text-on-surface-variant">
              Download the agent once per machine. No browser extension required.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {downloadOptions.map((option) => (
              <a
                key={option.label}
                href={option.href}
                download={resolveDownloadName(option.href, option.label)}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-on-primary shadow-xs transition-transform hover:opacity-90 active:scale-95"
              >
                <span className="material-symbols-outlined text-sm">{option.icon}</span>
                {option.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Interactive Tabs */}
      <div className="flex border-b border-outline-variant/10 bg-surface-container-lowest text-xs font-bold">
        {['Windows', 'macOS', 'Browser Network'].map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`flex flex-1 items-center justify-center gap-1.5 border-b-2 py-2.5 transition-colors ${
              activeTab === tab
                ? 'border-primary bg-primary/5 text-primary font-extrabold'
                : 'border-transparent text-on-surface-variant hover:text-on-surface hover:bg-surface-container-low'
            }`}
          >
            <span className="material-symbols-outlined text-base">
              {tab === 'Windows' ? 'desktop_windows' : tab === 'macOS' ? 'laptop_mac' : 'lan'}
            </span>
            {tab} Instructions
          </button>
        ))}
      </div>

      {/* Tab Contents */}
      <div className="p-6 text-sm text-on-surface-variant leading-relaxed">
        {/* Windows Tab */}
        {activeTab === 'Windows' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                1
              </span>
              <div>
                <p className="font-semibold text-on-surface">Download & Run the Windows Installer</p>
                <p className="text-xs text-on-surface-variant/80">
                  Click the <strong>Windows (.exe)</strong> download button above. Once downloaded, double-click the file to launch.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                2
              </span>
              <div>
                <p className="font-semibold text-on-surface">If Windows SmartScreen Prompts</p>
                <p className="text-xs text-on-surface-variant/80">
                  Click <strong>&quot;More info&quot;</strong>, then click <strong>&quot;Run anyway&quot;</strong>. The agent runs safely with standard user permissions.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                3
              </span>
              <div>
                <p className="font-semibold text-on-surface">Verify Agent is Running</p>
                <p className="text-xs text-on-surface-variant/80">
                  Look for the <strong>MMT Activity Agent</strong> icon in your Windows taskbar system tray (bottom-right).
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                4
              </span>
              <div>
                <p className="font-semibold text-on-surface">Complete Verification</p>
                <p className="text-xs text-on-surface-variant/80">
                  Click <strong>Start Device Access Check</strong> below to proceed to your dashboard.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* macOS Tab */}
        {activeTab === 'macOS' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                1
              </span>
              <div>
                <p className="font-semibold text-on-surface">Install to Applications</p>
                <p className="text-xs text-on-surface-variant/80">
                  Download the <strong>macOS (.dmg)</strong>, open the disk image, and drag <strong>MMT Activity Agent</strong> into your <strong>/Applications</strong> folder.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                2
              </span>
              <div>
                <p className="font-semibold text-on-surface">Grant Accessibility Permission (Required)</p>
                <p className="text-xs text-on-surface-variant/80">
                  Open <strong>Apple Menu  → System Settings → Privacy & Security → Accessibility</strong>.
                  Toggle <strong>MMT Activity Agent</strong> to <strong>ON (blue)</strong> so the agent can inspect active window titles.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                3
              </span>
              <div>
                <p className="font-semibold text-on-surface">If Unsigned Gatekeeper Warning Appears</p>
                <p className="text-xs text-on-surface-variant/80 mb-2">
                  For testing builds, run this command once in Terminal to remove the macOS download quarantine:
                </p>
                <div className="flex items-center gap-2 rounded-md bg-surface-container-lowest p-2 font-mono text-xs text-on-surface outline outline-1 outline-outline-variant/20">
                  <code className="flex-1 overflow-x-auto select-all">
                    {SYSTEM_VERIFICATION_COPY.setupMacTestingCommand}
                  </code>
                  <button
                    type="button"
                    onClick={handleCopyTerminal}
                    className="inline-flex items-center gap-1 rounded bg-surface-container-high px-2 py-1 text-[11px] font-bold text-primary hover:bg-primary/10 transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">
                      {copiedTerminal ? 'check' : 'content_copy'}
                    </span>
                    {copiedTerminal ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                4
              </span>
              <div>
                <p className="font-semibold text-on-surface">Complete Verification</p>
                <p className="text-xs text-on-surface-variant/80">
                  Launch the agent from Applications, then click <strong>Start Device Access Check</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Browser Network Access Tab */}
        {activeTab === 'Browser Network' && (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                1
              </span>
              <div>
                <p className="font-semibold text-on-surface">Private Network Access Prompt</p>
                <p className="text-xs text-on-surface-variant/80">
                  Modern browsers (Chrome 137+, Edge) may prompt: <em>&quot;Allow site to find and communicate with devices on your local network&quot;</em>. Always choose <strong>Allow</strong>.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                2
              </span>
              <div>
                <p className="font-semibold text-on-surface">How to Unblock if Blocked</p>
                <p className="text-xs text-on-surface-variant/80">
                  1. Click the <strong>Tune / Lock icon (🔒)</strong> on the left side of the address bar.<br />
                  2. Open <strong>Site Settings</strong>.<br />
                  3. Find <strong>Local network access</strong> (or <strong>Apps on device</strong>) and set it to <strong>Allow</strong>.<br />
                  4. Reload this page and click <strong>Retry Verification</strong>.
                </p>
              </div>
            </div>

            <div className="rounded-md bg-primary/5 p-3 text-xs text-on-surface-variant border border-primary/10">
              <p className="font-semibold text-primary mb-0.5">🔒 Enterprise Security Note</p>
              Bridge only connects to <code>http://localhost:4000</code> strictly on your own device to sign a cryptographic challenge. No network ports are exposed to the outside internet.
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
